import React, { useMemo } from "react";
import { useSelector } from "react-redux";

import KButton from "@kikoff/components/src/v1/buttons/KButton";
import Card from "@kikoff/components/src/v1/cards/Card";
import { ChipV2 } from "@kikoff/components/src/v1/info/Chip";
import IconList from "@kikoff/components/src/v1/info/IconList";
import OneCardOf from "@kikoff/components/src/v1/inputs/OneCardOf";
import { cls, format } from "@kikoff/utils/src/string";

import {
  UPGRADE_PROMO_FIVE_DOLLARS,
  UPGRADE_PROMO_TEN_DOLLARS,
} from "@constant/promos";
import { selectFeatureFlag } from "@feature/page";
import { selectChangeSubscriptionPlanPreview } from "@feature/shopping";
import { Plan } from "@feature/subscription";
import { selectPromo } from "@feature/user";
import { getPlanIconItems } from "@page/dashboard/credit-account/_views/plan";
import { useOverlaysController } from "@src/overlay";
import { l10nResources } from "@util/l10n";

import styles from "./styles.module.scss";

const l10n = l10nResources({
  en: `
basic = Basic
premium = Premium
ultimate = Ultimate
month-abbr = mo
continue = Continue
`,
  es: `
basic = Básica
premium = Premium
ultimate = Ultimate
month-abbr = mes
continue = Continuar
`,
});

type planDetails = {
  key: Plan;
  iconKey: string;
  title: string;
  creditLine: number;
  tag?: React.ReactNode;
};

const allPlans: planDetails[] = [
  {
    key: "ultimate",
    iconKey: "Ultimate",
    title: l10n.getString("ultimate"),
    creditLine: 3500,
    tag: (
      <ChipV2
        variant="round"
        className={styles["ultimate-chip"]}
        color="transparent"
        size="mini"
      >
        <div className="color:strong">Best value</div>
      </ChipV2>
    ),
  },
  {
    key: "premium",
    iconKey: "Premium",
    title: l10n.getString("premium"),
    creditLine: 2500,
    tag: (
      <ChipV2 variant="round" color="var(--primary-strong)" size="mini">
        Most popular
      </ChipV2>
    ),
  },
  {
    key: "basic",
    iconKey: "Basic",
    title: l10n.getString("basic"),
    creditLine: 750,
  },
];

interface SelectPlanSectionProps<P extends Plan> {
  plansToShow?: P[];
  selectedPlanKey: NoInfer<P>;
  setSelectedPlanKey: (plan: NoInfer<P>) => void;
  existingPlanKey?: Plan; // for change plan page, to show current plan chip
  source?: string;
  context?: "default" | "dispute_delivery";
}

function SelectPlanSection<P extends Plan = Plan>({
  plansToShow = ["ultimate", "premium", "basic"] as P[],
  selectedPlanKey,
  setSelectedPlanKey,
  existingPlanKey,
  source,
  context,
}: SelectPlanSectionProps<P>) {
  const overlays = useOverlaysController();

  const planInformation = Object.fromEntries(
    plansToShow.map((plan) => {
      const [planPreview] = useSelector(
        selectChangeSubscriptionPlanPreview.load(plan)
      );
      return [plan, planPreview];
    })
  );

  const plans = useMemo(() => {
    return allPlans.filter((plan) => plansToShow.includes(plan.key as P));
  }, []);

  const selectedPlan =
    plans.find((plan) => plan.key === selectedPlanKey) ?? plans[0];

  const planItems = getPlanIconItems(
    selectedPlan.iconKey,
    selectedPlan.creditLine * 100,
    context
  ).filter(Boolean);

  const upgradePromoExperiment = useSelector(
    selectFeatureFlag("upgrade_promo_experiment")
  );

  const promo = useSelector(
    (state) =>
      selectPromo(UPGRADE_PROMO_FIVE_DOLLARS)(state) ||
      selectPromo(UPGRADE_PROMO_TEN_DOLLARS)(state)
  );
  const discount = promo?.monthlyCreditAmountCents;

  if (selectedPlan.iconKey !== "Basic") {
    planItems.push(
      <>
        Kikoff Secured Card Access
        <div className={cls("text:mini", styles["icon-item-subtitle"])}>
          Get spotted up to $200
        </div>
      </>
    );
  }
  return (
    <div>
      <OneCardOf selected={selectedPlan.key} onChange={setSelectedPlanKey}>
        {plans.map((option) => (
          <Card
            key={option.key}
            role="radio"
            className={cls("py-2", styles["plan-card"])}
          >
            <div>
              <div className={styles["plan-title"]}>
                <div className="text:large+">{option.title}</div>
                {existingPlanKey && option.key === existingPlanKey ? (
                  <ChipV2
                    variant="round"
                    size="mini"
                    color="var(--container)"
                    raised
                  >
                    <div className="color:strong">Current plan</div>
                  </ChipV2>
                ) : (
                  option.tag
                )}
              </div>
              <div className="text:regular+ pt-1">
                {format.money(
                  planInformation[option.key]?.product.monthlyPaymentCents,
                  {
                    includeCents:
                      planInformation[option.key]?.product.monthlyPaymentCents %
                        100 !==
                      0,
                  }
                )}
                /{l10n.getString("month-abbr")}
              </div>
              {upgradePromoExperiment &&
                (promo?.name === UPGRADE_PROMO_FIVE_DOLLARS ||
                  promo?.name === UPGRADE_PROMO_TEN_DOLLARS) &&
                (source === "bureau_tabs" ||
                  source === "credit_account_upsell") && (
                  <div className="text:small color:primary">
                    {format.money(
                      planInformation[option.key]?.product.monthlyPaymentCents -
                        discount
                    )}{" "}
                    for the first month
                  </div>
                )}
            </div>
            <input
              type="radio"
              key={`${option.key}-radio`}
              checked={selectedPlan.key === option.key}
              readOnly
            />
          </Card>
        ))}
      </OneCardOf>

      <div className={styles["plan-card"]}>
        <div className="text:regular+">{selectedPlan.title} includes</div>
        {!existingPlanKey && plans.length > 1 && (
          <KButton
            variant="text-underline"
            onClick={() =>
              overlays.push("credit_line/compare_plans", {
                currentPlan: selectedPlan.key as Plan,
                plans: plans.map((plan) => plan.key as Plan),
              })
            }
            size="small"
            className="text:mini+ color:moderate"
          >
            Compare plans
          </KButton>
        )}
      </div>
      <IconList
        className="my-2 text:small"
        color="var(--primary)"
        density="default"
        size="1rem"
        icon=""
        items={planItems}
      />
    </div>
  );
}

export default SelectPlanSection;
